import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import { UserRole } from 'src/constants';
import PROFILE_ITEM_SHORT_NAMES from 'src/constants/profileCompletion';
import { updateSelf } from '../actions/auth';
import {
  autofillProfileFromResume,
  changeResumeSetting,
  deleteAward,
  deleteCertification,
  deleteEducation,
  deleteExperience,
  deleteInterest,
  deleteLanguage,
  deletePositionOfResponsibility,
  deleteProject,
  deletePublication,
  deleteSocialLink,
  deleteUserResume,
  deleteVolunteerExperience,
  fetchCandidateDetails,
  fetchProfileCompletion,
  postAward,
  postCertification,
  postEducation,
  postExperience,
  postInterest,
  postLanguage,
  postPositionOfResponsibility,
  postProject,
  postPublication,
  postSocialLinks,
  postVolunteerExperience,
  setProfileItemSkipped,
  updatePreference,
  uploadResume,
} from '../actions/candidateProfile';
import type { RootState } from '..';
import type { ProfileCompletion } from '../types/candidateProfile';

// Every profile save that can change the checklist. The sections patch the
// profile in place, so without this the percent stayed where it was until the
// next full reload, and then jumped.
const isProfileSave = isAnyOf(
  postEducation.fulfilled,
  deleteEducation.fulfilled,
  postExperience.fulfilled,
  deleteExperience.fulfilled,
  postProject.fulfilled,
  deleteProject.fulfilled,
  postSocialLinks.fulfilled,
  deleteSocialLink.fulfilled,
  updatePreference.fulfilled,
  updateSelf.fulfilled,
  uploadResume.fulfilled,
  deleteUserResume.fulfilled,
  changeResumeSetting.fulfilled,
  autofillProfileFromResume.fulfilled,
  postCertification.fulfilled,
  deleteCertification.fulfilled,
  postAward.fulfilled,
  deleteAward.fulfilled,
  postLanguage.fulfilled,
  deleteLanguage.fulfilled,
  postPublication.fulfilled,
  deletePublication.fulfilled,
  postVolunteerExperience.fulfilled,
  deleteVolunteerExperience.fulfilled,
  postPositionOfResponsibility.fulfilled,
  deletePositionOfResponsibility.fulfilled,
  postInterest.fulfilled,
  deleteInterest.fulfilled,
);

const isCompletionUpdate = isAnyOf(
  fetchProfileCompletion.fulfilled,
  setProfileItemSkipped.fulfilled,
  fetchCandidateDetails.fulfilled,
);

/** "Profile 70% → 85% complete · Projects added" */
export const describeCompletionChange = (
  before: ProfileCompletion,
  after: ProfileCompletion,
) => {
  const previous = new Map(before.items.map((item) => [item.key, item]));
  const reasons = after.items.flatMap((item) => {
    const was = previous.get(item.key);
    const name = PROFILE_ITEM_SHORT_NAMES[item.key];
    if (!was) return [];
    if (!was.done && item.done) return [`${name} added`];
    if (was.done && !item.done) return [`${name} removed`];
    if (!was.skipped && item.skipped) return [`${name} marked not yet`];
    if (was.skipped && !item.skipped) return [`${name} counts again`];
    return [];
  });
  const change = `Profile ${before.percent}% → ${after.percent}% complete`;
  return reasons.length ? `${change} · ${reasons.join(', ')}` : change;
};

export const profileCompletionListener = createListenerMiddleware();

profileCompletionListener.startListening({
  matcher: isProfileSave,
  effect: async (_, api) => {
    // Employers save their own profile through updateSelf too; the checklist
    // is a candidate's.
    if (
      (api.getState() as RootState).auth.user?.user_type !== UserRole.Applicant
    )
      return;
    // A resume import saves every section at once: refresh once, after.
    api.cancelActiveListeners();
    await api.delay(300);
    api.dispatch(fetchProfileCompletion());
  },
});

// The number never moves without saying why.
profileCompletionListener.startListening({
  matcher: isCompletionUpdate,
  effect: (_, api) => {
    const before = (api.getOriginalState() as RootState).candidateProfile
      .profileCompletion;
    const after = (api.getState() as RootState).candidateProfile
      .profileCompletion;
    if (!before || !after || before.percent === after.percent) return;
    enqueueSnackbar(describeCompletionChange(before, after), {
      variant: after.percent > before.percent ? 'success' : 'info',
    });
  },
});
